import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CHAT_ANIMATIONS } from '@/utils/chat-animations';
import { Image, Plus, Check, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import GlassPill from '@/components/ui/GlassPill';
import { useAgentChatImageGenerationWithTracking } from '@/hooks/ai-agents/functions/core/useAgentChatImageGenerationWithTracking';
import { useRecommendationCardState, RECOMMENDATION_CONFIGS } from '@/hooks/ai-agents/functions/core/createRecommendationTrackingWrapper';
import { useRecommendationTracking } from '@/hooks/ai-agents/functions/core/useRecommendationTracking';
import { useViewRecommendationNavigation } from '@/hooks/ai-agents/functions/core/useViewRecommendationNavigation';
import { useThreadContext } from '@/hooks/ai-agents/functions/core/useThreadContext';
import { useNavigate } from 'react-router-dom';
import { 
  getProjectInfoById,
  getProjectNavigationUrl,
  getGoalNavigationUrl,
  useNavigationWithModalClose
} from '@/hooks/ai-agents/functions/core/useResponseParsing';
import { cn } from '@/lib/utils';
import { ExtractedImage } from '@/hooks/ai-agents/functions/core/useRecommendationDetection';
import { MessageDisplay } from '@/hooks/ai-agents/types/chat-hook-types';
import { 
  getCardContainerClasses, 
  getCardIconClasses, 
  getCardButtonClasses 
} from './shared/RecommendationCardStyles';

interface ChatRecommendedImageCardProps {
  image: ExtractedImage;
  threadId: string;
  message: MessageDisplay; // Changed from messageId: string to message: MessageDisplay
  type?: 'image';
  className?: string;
}

type SaveState = 'idle' | 'saving' | 'success' | 'saved' | 'error';

/**
 * Chat Recommended Image Card - Individual image item with save button
 * Shows image recommendations with option to generate and save to project workbook
 * Now with standardized recommendation tracking
 */
export const ChatRecommendedImageCard: React.FC<ChatRecommendedImageCardProps> = ({
  image,
  threadId,
  message, // Changed from messageId to message
  type = 'image',
  className
}) => {
  const { navigateAndCloseModal } = useNavigationWithModalClose();
  const [savedImageUrl, setSavedImageUrl] = useState<string | null>(null);
  const [imageLoadError, setImageLoadError] = useState(false);
  
  // Get thread context to find the workbook ID
  const { data: threadContext } = useThreadContext(threadId);
  
  // Use standardized tracking state management with full message object
  const { localState, setLocalState } = useRecommendationCardState(message, 'image');
  
  // Image generation with tracking using full message object
  const { mutateWithTracking, isGenerating } = useAgentChatImageGenerationWithTracking(message);
  
  // Get saved recommendation data for view functionality using full message object
  const { isRecommendationSaved } = useRecommendationTracking(message.id, message.databaseId);
  
  // View navigation functionality
  const { viewImage } = useViewRecommendationNavigation();
  
  // Load saved image URL when in saved state
  useEffect(() => {
    const loadSavedImageUrl = async () => {
      if (localState === 'saved') {
        try {
          const status = await isRecommendationSaved('image');
          if (status.isSaved && status.metadata?.imageUrl) {
            setSavedImageUrl(status.metadata.imageUrl);
            setImageLoadError(false); // Reset error state
          }
        } catch (error) {
          console.warn('[ChatRecommendedImageCard] Failed to load saved image URL:', error);
        }
      }
    };
    
    loadSavedImageUrl();
  }, [localState, isRecommendationSaved]);
  
  // Handle viewing the saved image
  const handleViewImage = async () => {
    const status = await isRecommendationSaved('image');
    if (status.isSaved && status.savedItemId && status.metadata) {
      const savedRecommendation = {
        id: status.savedItemId,
        type: 'image' as const,
        savedAt: status.savedAt!,
        metadata: status.metadata
      };
      viewImage(savedRecommendation);
    } else {
      console.error('[ChatRecommendedImageCard] No saved image data found');
    }
  };
  
  // Handle image load error (when temporary URL breaks)
  const handleImageError = () => {
    console.log('[ChatRecommendedImageCard] Image failed to load (likely temporary URL expired)');
    setImageLoadError(true);
  };
  
  // Get config for this recommendation type
  const config = RECOMMENDATION_CONFIGS.image;
  
  // Images now always render and go to private image library (no project dependencies)

  // Handle saving this specific image
  const handleSaveImage = async () => {
    console.log('[ChatRecommendedImageCard] 🚀 STARTING handleSaveImage - Private Image');
    
    setLocalState('saving');
    
    try {
      const result = await mutateWithTracking({
        image, // Pass the full image object as expected by the hook
        messageId: message.id // Use message.id for consistency
        // No workbookId or goalThreadId - private image
      });
      
      setLocalState('saved');
      
    } catch (error) {
      console.error('[ChatRecommendedImageCard] Failed to save image:', error);
      setLocalState('error');
      
      // Reset to idle after 3 seconds
      setTimeout(() => {
        setLocalState('idle');
      }, 3000);
    }
  };

  // Get button content based on state
  const getButtonContent = () => {
    switch (localState) {
      case 'saving':
        return <Loader2 size={12} className="animate-spin" />;
      case 'success':
        return <Check size={12} />;
      case 'saved':
        return <Image size={12} />;
      case 'error':
        return <AlertCircle size={12} />;
      default:
        return <Plus size={12} />;
    }
  };

  // Get button text based on state
  const getButtonText = () => {
    switch (localState) {
      case 'saving':
        return config.savingText;
      case 'success':
        return config.successText;
      case 'saved':
        return config.savedText;
      case 'error':
        return config.errorText;
      default:
        return config.saveText;
    }
  };

  // Get button click handler based on state
  const getButtonHandler = () => {
    switch (localState) {
      case 'saved':
        return handleViewImage;
      default:
        return handleSaveImage;
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        variants={CHAT_ANIMATIONS.cardEntry}
        initial="initial"
        animate="animate"
        exit="exit"
        className={cn("mt-3 w-full", className)}
      >
        <div className={cn(getCardContainerClasses(type))}>
          <div className="flex items-start gap-2.5">
            {/* Save Button - Left Side */}
            <div className="flex-shrink-0 pt-0.5">
              <Button
                size="sm"
                variant="outline"
                onClick={getButtonHandler()}
                disabled={localState === 'saving' || localState === 'success'}
                className={cn(
                  "h-7 px-3 text-xs border-blue-300 dark:border-blue-600",
                  "hover:bg-blue-50 dark:hover:bg-blue-900/20",
                  "transition-all duration-200",
                  // State-specific styling
                  localState === 'success' && 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400',
                  localState === 'saved' && 'border-gray-300 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200',
                  localState === 'error' && 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400',
                )}
              >
                <span className="flex items-center gap-1.5">
                  {getButtonContent()}
                  <span>{getButtonText()}</span>
                </span>
              </Button>
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm text-gray-700 dark:text-gray-200 block font-medium">
                  Image Generation
                </span>
                <div className="flex gap-1">
                  <GlassPill type={image.style} variant="blue" />
                  <GlassPill type={image.size} variant="blue" />
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mb-2">
                {image.description}
              </p>
              
              {/* Private Image - No project navigation needed */}
              
              {/* Show generated image preview if available and in saved state */}
              {localState === 'saved' && savedImageUrl && !imageLoadError && (
                <div 
                  className="mt-2 rounded-lg overflow-hidden border border-blue-200 dark:border-blue-700 max-w-[300px] cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={handleViewImage}
                >
                  <img 
                    src={savedImageUrl} 
                    alt={image.description}
                    className="w-full object-cover"
                    loading="lazy"
                    onError={handleImageError}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}; 